import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import React from 'react'
import { IResultQueryReservationJoinOnUserAndChargeAndVehicle } from '../../services/BookingChecking/bookingCheckin.interface'
import { getSuccessfulCharge } from '../../utils/receiptGenerator'

interface PaymentReceiptComponentProps {
  reservationInfo: IResultQueryReservationJoinOnUserAndChargeAndVehicle | null
  matches: boolean
  onDownloadReceipt: () => void
}

const PaymentReceiptComponent: React.FC<PaymentReceiptComponentProps> = ({
  reservationInfo,
  matches,
  onDownloadReceipt,
}) => {
  const charge = getSuccessfulCharge(reservationInfo)

  const chargeData: any[] = (reservationInfo?.reservationData as any)?.chargeData || []
  const preCheckinCharged = chargeData.filter(
    (c) => c.description?.includes('PRE-CHECKIN SYSTEM') && c.chargestate?.includes('CHARGED'),
  )
  const depositCharge = preCheckinCharged.find((c) => c.charge_deposit > 0)
  const feeCharge = preCheckinCharged.find((c) => c.charge_paymentfee > 0)
  const depositAmount: number = depositCharge?.charge_deposit || 0
  const feeAmount: number = feeCharge?.charge_paymentfee || 0
  const totalAmount = depositAmount + feeAmount

  return (
    <div
      style={{
        width: matches ? '100%' : '100%',
        backgroundColor: '#fff',
        margin: matches ? '2rem auto' : '2rem 0',
        maxWidth: matches ? '600px' : 'none',
        borderRadius: matches ? '12px' : '8px',
        boxShadow: matches ? '0 4px 20px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      {/* Receipt Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
          color: '#fff',
          padding: matches ? '24px' : '20px',
          textAlign: 'center',
        }}
      >
        <CheckCircleOutlined style={{ fontSize: matches ? '32px' : '28px', marginBottom: '8px' }} />
        <div
          style={{
            fontSize: matches ? '20px' : '18px',
            fontWeight: '600',
            marginBottom: '4px',
          }}
        >
          Payment Successful!
        </div>
        <div style={{ fontSize: matches ? '14px' : '12px', opacity: 0.9 }}>
          Your deposit has been processed successfully
        </div>
      </div>

      {/* Receipt Details */}
      <div style={{ padding: matches ? '24px' : '20px' }}>
        {/* Transaction Info */}
        <div style={{ marginBottom: '20px' }}>
          <div
            style={{
              fontSize: matches ? '16px' : '14px',
              fontWeight: '600',
              color: '#333',
              marginBottom: '12px',
            }}
          >
            Transaction Details
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>
              Transaction ID:
            </span>
            <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
              {depositCharge?.chargeid || charge?.chargeid || 'N/A'}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>
              Payment Method:
            </span>
            <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
              {charge?.chargemethod || 'N/A'}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>Status:</span>
            <span
              style={{
                color: '#52c41a',
                fontWeight: '600',
                fontSize: matches ? '14px' : '13px',
              }}
            >
              {charge?.chargestate || 'CHARGED'}
            </span>
          </div>

          {depositAmount > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>Deposit:</span>
              <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
                {depositAmount.toLocaleString()} THB
              </span>
            </div>
          )}
          {feeAmount > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>
                Payment fee (3%):
              </span>
              <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
                {feeAmount.toLocaleString()} THB
              </span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>
              Total charged:
            </span>
            <span
              style={{
                color: '#0048B3',
                fontWeight: '700',
                fontSize: matches ? '18px' : '16px',
              }}
            >
              {(totalAmount || charge?.chargetotal || 0).toLocaleString()} THB
            </span>
          </div>
        </div>

        {/* Reservation Info */}
        <div style={{ marginBottom: '20px' }}>
          <div
            style={{
              fontSize: matches ? '16px' : '14px',
              fontWeight: '600',
              color: '#333',
              marginBottom: '12px',
            }}
          >
            Reservation Information
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>
              Reservation No:
            </span>
            <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
              {reservationInfo?.reservationData?.reservationno || 'N/A'}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>Vehicle:</span>
            <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
              {reservationInfo?.reservationData?.vehicleData
                ? `${reservationInfo.reservationData.vehicleData.vehiclebrand} ${reservationInfo.reservationData.vehicleData.vehiclemodel}`
                : 'N/A'}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
            }}
          >
            <span style={{ color: '#666', fontSize: matches ? '14px' : '13px' }}>
              Pick-up Location:
            </span>
            <span style={{ fontWeight: '500', fontSize: matches ? '14px' : '13px' }}>
              {reservationInfo?.stationDetail?.name || 'N/A'}
            </span>
          </div>
        </div>

        {/* Download Button */}
        <div
          style={{
            textAlign: 'center',
            paddingTop: '16px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={onDownloadReceipt}
            style={{
              backgroundColor: '#0048B3',
              borderColor: '#0048B3',
              borderRadius: matches ? '8px' : '6px',
              height: matches ? '40px' : '36px',
              fontSize: matches ? '14px' : '13px',
              fontWeight: '500',
            }}
          >
            Download Receipt
          </Button>
          <div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
            Save this receipt as proof of payment
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentReceiptComponent
